import { Add, Delete, Edit, Search } from "@mui/icons-material";
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import CustomerDisplayName from '../../components/CustomerDisplayName';
import SalesChannelSelect from '../../components/checkout/SalesChannelSelect';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import {
    formatCurrencyInCents,
    formatDate,
    formatDocument,
    presentPhone,
    presentUuid,
} from "../../helpers/formatters";
import { applyEmail } from '../../helpers/captureMode';
import {
    CUSTOMER_CATEGORY_OPTIONS,
    DEFAULT_CUSTOMER_SORT_BY,
    DEFAULT_CUSTOMER_SORT_ORDER,
} from "../../helpers/customers";
import { customerOrdersPath } from "../../helpers/orders";

function ListCustomersPage() {
    const [customers, setCustomers] = useState([]);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [draftSearchTerm, setDraftSearchTerm] = useState('');
    const [draftCategory, setDraftCategory] = useState('');
    const [draftOriginSalesChannelUuid, setDraftOriginSalesChannelUuid] = useState('');
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState('');
    const [activeOriginSalesChannelUuid, setActiveOriginSalesChannelUuid] = useState('');
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [sortBy, setSortBy] = useState(DEFAULT_CUSTOMER_SORT_BY);
    const [sortOrder, setSortOrder] = useState(DEFAULT_CUSTOMER_SORT_ORDER);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchCustomers = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: String(currentPage),
            limit: String(itemsPerPage),
            sort_by: sortBy,
            sort_order: sortOrder,
        });
        if (activeSearchQuery) {
            params.set('q', activeSearchQuery);
        }
        if (activeCategory) {
            params.set('category', activeCategory);
        }
        if (activeOriginSalesChannelUuid) {
            params.set('origin_sales_channel_uuid', activeOriginSalesChannelUuid);
        }
        api.get(`customers?${params.toString()}`)
            .then(response => {
                setCustomers(response.data.customers);
                setTotalPages(response.data.total_pages);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, activeSearchQuery, activeCategory, activeOriginSalesChannelUuid, sortBy, sortOrder]);

    useListFetch(fetchCustomers, [currentPage, itemsPerPage, activeSearchQuery, activeCategory, activeOriginSalesChannelUuid, sortBy, sortOrder, fetchCustomers]);

    const resetCustomerSort = () => {
        setSortBy(DEFAULT_CUSTOMER_SORT_BY);
        setSortOrder(DEFAULT_CUSTOMER_SORT_ORDER);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(field);
            setSortOrder(field === DEFAULT_CUSTOMER_SORT_BY ? DEFAULT_CUSTOMER_SORT_ORDER : 'asc');
        }
        setCurrentPage(1);
    };

    const handleApplyFilters = () => {
        const nextQuery = draftSearchTerm.trim();
        setActiveSearchQuery(nextQuery);
        setActiveCategory(draftCategory || '');
        setActiveOriginSalesChannelUuid(draftOriginSalesChannelUuid || '');
        setFiltersApplied(Boolean(nextQuery) || Boolean(draftCategory) || Boolean(draftOriginSalesChannelUuid));
        setCurrentPage(1);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
        resetCustomerSort();
    };

    const handleClearFilters = () => {
        setDraftSearchTerm('');
        setDraftCategory('');
        setDraftOriginSalesChannelUuid('');
        setActiveSearchQuery('');
        setActiveCategory('');
        setActiveOriginSalesChannelUuid('');
        setFiltersApplied(false);
        setCurrentPage(1);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
        resetCustomerSort();
    };

    const handleCreateCustomer = () => {
        navigate('/admin/register-customer');
    };

    const handleEditCustomer = (id) => {
        navigate(`/admin/edit-customer/${id}`);
    };

    const handleDeleteCustomer = (customerId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este cliente será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed) {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`customers/${customerId}`)
                        .then(() => {
                            setCustomers(customers.filter((customer) => customer.uuid !== customerId));
                            Swal.fire('Removido!', 'Cliente removido com sucesso.', 'success');
                            setLoadingDelete(false);
                        })
                        .catch(() => {
                            Swal.fire('Erro!', 'Houve um problema ao remover o cliente.', 'error');
                            setLoadingDelete(false);
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Clientes</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                <Typography variant="h4" gutterBottom>
                    Clientes
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 160 }}>
                        <InputLabel id="customer-list-category-label">Tipo</InputLabel>
                        <Select
                            labelId="customer-list-category-label"
                            label="Tipo"
                            value={draftCategory}
                            onChange={(e) => setDraftCategory(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {CUSTOMER_CATEGORY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ minWidth: isMobile ? '100%' : 180, width: isMobile ? '100%' : 180 }}>
                        <SalesChannelSelect
                            label="Origem"
                            value={draftOriginSalesChannelUuid}
                            onChange={(e) => setDraftOriginSalesChannelUuid(e.target.value)}
                            allowEmpty
                            emptyLabel="Todas"
                            size="small"
                        />
                    </Box>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar cliente..."
                        value={draftSearchTerm}
                        onChange={(e) => setDraftSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApplyFilters}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Aplicar filtro
                    </Button>
                    {filtersApplied && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar filtro
                        </Button>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center', gap: 1 }}>
                    <PageTutorialButtons pageKey="customers" fullWidth={isMobile} />
                    <QuotaLimitedButton
                        quotaKey="customers"
                        variant="contained"
                        color="primary"
                        onClick={handleCreateCustomer}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Cliente
                    </QuotaLimitedButton>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'name'}
                                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    Nome
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contato</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'orders_count'}
                                    direction={sortBy === 'orders_count' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('orders_count')}
                                >
                                    Compras
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'average_ticket_cents'}
                                    direction={sortBy === 'average_ticket_cents' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('average_ticket_cents')}
                                >
                                    Ticket médio
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === 'last_order_at'}
                                    direction={sortBy === 'last_order_at' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('last_order_at')}
                                >
                                    Última compra
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                    active={sortBy === DEFAULT_CUSTOMER_SORT_BY}
                                    direction={sortBy === DEFAULT_CUSTOMER_SORT_BY ? sortOrder : DEFAULT_CUSTOMER_SORT_ORDER}
                                    onClick={() => handleSort(DEFAULT_CUSTOMER_SORT_BY)}
                                >
                                    Cadastro
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Origem</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Em aberto</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(customers) && customers.length > 0 && customers.map((customer) => {
                            const unpaid = Number(customer.unpaid_amount_cents) || 0;
                            const ordersCount = Number(customer.orders_count) || 0;
                            const ordersHref = customerOrdersPath(customer);
                            return (
                                <TableRow key={customer.uuid}>
                                    <TableCell>
                                        <CustomerDisplayName customer={customer} variant="body2" fontWeight={600} />
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {presentUuid(customer.uuid)}
                                            {customer.document ? ` · ${formatDocument(customer.document)}` : ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{presentPhone(customer.phone) || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {applyEmail(customer.email) || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {ordersCount > 0 ? (
                                            <>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {ordersCount} compra{ordersCount === 1 ? '' : 's'}
                                                </Typography>
                                                <Typography
                                                    component={Link}
                                                    to={ordersHref}
                                                    variant="caption"
                                                    color="primary"
                                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    {formatCurrencyInCents(customer.orders_total_cents)}
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">Nenhuma</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {ordersCount > 0 ? (
                                            <Typography
                                                component={Link}
                                                to={ordersHref}
                                                variant="body2"
                                                color="primary"
                                                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {formatCurrencyInCents(customer.average_ticket_cents)}
                                            </Typography>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {customer.last_order_at ? (
                                            <Typography
                                                component={Link}
                                                to={ordersHref}
                                                variant="body2"
                                                color="primary"
                                                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {formatDate(customer.last_order_at)}
                                            </Typography>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {customer.created_at ? formatDate(customer.created_at) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {customer.origin_sales_channel_name ? (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={customer.origin_sales_channel_name}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {unpaid > 0 ? (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                label={formatCurrencyInCents(unpaid)}
                                                title={`${customer.unpaid_orders_count || 0} compra(s) não paga(s)`}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">Em dia</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEditCustomer(customer.uuid)}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteCustomer(customer.uuid)}
                                            aria-label="remover cliente"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListCustomersPage;
